import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../config/constants';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import { useLibrary } from '../../hooks/useLibrary';
import { PatternCard } from './PatternCard';
import { Carousel } from '../LandingPage/Carousel';
import {
	COLOR_BROWN_PRIMARY,
	COLOR_PURPLE_LINK,
	GRADIENT_RAINBOW,
	GRADIENT_RAINBOW_FADED,
	GRADIENT_LIBRARY_INFO_BG,
	SHADOW_CARD_SUBTLE,
} from '../../styles/tokens';

const LibraryGrid = styled.div`
	display: grid;
	gap: 16px;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	max-width: 1200px;
	margin: 0 auto 32px;
`;

const TitleWrapper = styled.div`
	text-align: center;
`;
const SectionTitle = styled.h2`
	font-size: 3.5rem;
	font-weight: 600;
	margin: 0;
	margin-top: -10px;
	letter-spacing: 0.05em;

	& > span {
		display: inline-block; /* makes gradient width match the text */
		background: linear-gradient(
			90deg,
			#ff7b00 0%,
			#ff2d55 30%,
			#b027ff 65%,
			#2db2ff 100%
		);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text; /* newer non-webkit engines */
		color: transparent; /* fallback */
		line-height: 1.1; /* crisp edges */
	}
`;
const StyledSearch = styled(TextField)`
	margin: 16px 0 !important;
	margin-top: 0px !important;
	& .MuiInputBase-root {
		font-size: 1.5rem;
		height: 62px;
	}
	& .MuiInputLabel-root {
		font-size: 1.5rem;
	}
`;

export const PatternLibrary = () => {
	const navigate = useNavigate();
	const {
		patterns,
		loading,
		searchError,
		searchTerm,
		setSearchTerm,
		totalPages,
		currentPage,
		goToPage,
	} = useLibrary();

	return (
		<>
			<TitleWrapper>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						marginTop: '10px',
						cursor: 'pointer',
					}}
					onClick={() => navigate(ROUTES.ABOUT)}
				>
					<img
						src={process.env.PUBLIC_URL + '/Logo.png'}
						alt="Fore Edge Book Art"
						style={{
							maxWidth: '50px',
							maxHeight: '50px',
						}}
					/>

					<Typography
						variant="h3"
						component="h2"
						sx={{
							fontWeight: 500,
							letterSpacing: '1px',
							color: COLOR_BROWN_PRIMARY,
						}}
					>
						Fore Edge Book Art
					</Typography>
				</div>
			</TitleWrapper>

			<Container>
				<Box
					sx={{
						background: GRADIENT_LIBRARY_INFO_BG,
						borderRadius: '16px',
						padding: { xs: '20px', md: '32px' },
						maxWidth: '900px',
						margin: '30px auto',
						marginTop: '18px',
						boxShadow: SHADOW_CARD_SUBTLE,
						textAlign: 'center',
					}}
				>
					<SectionTitle>
						<span>Pattern Library</span>
					</SectionTitle>

					<Typography
						variant="h5"
						sx={{
							color: COLOR_BROWN_PRIMARY,
							lineHeight: 1,
							fontWeight: 400,
							maxWidth: '800px',
							margin: '0 auto',
						}}
					>
						The Fore Edge Book Art Pattern Library is a growing
						collection of individual fore edge book art patterns —
						available for instant download to use in your own
						projects. Each pattern is designed to work seamlessly
						with the{' '}
						<Link
							href="/patternmaker"
							underline="hover"
							sx={{
								color: COLOR_PURPLE_LINK,
								fontWeight: 600,
							}}
						>
							Fore Edge Book Art Pattern Maker
						</Link>
						, allowing you to create a custom fit for your own book.
						Browse free and premium patterns, explore by theme or
						style, and bring your books to life with beautiful,
						ready-to-use fore edge book art patterns.
					</Typography>
				</Box>

				<StyledSearch
					label="Search Patterns"
					fullWidth
					variant="outlined"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
				/>
				<Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={(_, value) => goToPage(value)}
						siblingCount={1}
						boundaryCount={1}
						shape="rounded"
						disabled={loading}
					/>
				</Stack>

				{loading ? (
					<Stack alignItems="center" sx={{ my: 4 }}>
						<CircularProgress />
					</Stack>
				) : searchError ? (
					<Typography
						sx={{ textAlign: 'center', my: 4, color: 'error.main' }}
					>
						{searchError}
					</Typography>
				) : (
					<LibraryGrid>
						{patterns.map((pattern) => (
							<PatternCard key={pattern.id} pattern={pattern} />
						))}
					</LibraryGrid>
				)}
			</Container>

			{/* Callout: Use your own image */}
			<Stack
				alignItems="center"
				sx={{ mt: 6, mb: 4, textAlign: 'center' }}
			>
				<Typography
					variant="h5"
					sx={{
						color: COLOR_BROWN_PRIMARY,
						fontWeight: 400,
					}}
				>
					Want to use your own image to make custom patterns?
				</Typography>

				<Link
					href="/patternmaker"
					underline="none"
					sx={{
						fontSize: '1.5rem',
						fontWeight: 600,
						letterSpacing: '0.03em',
						background: GRADIENT_RAINBOW,
						WebkitBackgroundClip: 'text',
						WebkitTextFillColor: 'transparent',
						position: 'relative',
						display: 'inline-block',
						'&::after': {
							content: '""',
							position: 'absolute',
							left: 0,
							right: 0,
							bottom: 1,
							height: 3,
							borderRadius: 2,
							background: GRADIENT_RAINBOW_FADED,
							transform: 'scaleX(0)',
							transformOrigin: 'left',
							transition: 'transform 200ms ease',
						},
						'&:hover::after': { transform: 'scaleX(1)' },
					}}
				>
					Head over to the Pattern Maker →
				</Link>
			</Stack>

			<div style={{ marginTop: '50px' }}>
				<Carousel />
			</div>
		</>
	);
};
